import Logger from 'core/utils/Logger'

import {
  F_QUARTERS_FULL,
  F_QUARTERS_ONLY,
  finSobValidationGroups,
  FYEAR_OFFSETS,
  IS_CHECK_LAST_PREV_VS_FUTURE,
  IS_CHECK_SELF_IN_FUTURE_FQ,
  OVERALL_CATEGORY,
  PLANNING_BY_FY_FQ,
} from '../../config'
import { TSType } from '../../models'
import { CustomFields } from '../../models/CustomFields'
import { IFinSobCheckItem, IFinSobValidationGroup } from '../../models/IFinSobCheckItem'
import { IFinSobValidationError } from '../../models/IFinSobValidationError'
import { calcPercentage } from '../calcPercentage'
import { getFinSobValue, IGetFinSobValueProps } from '../getFinSobValue'

import { addFinSobError } from './addFinSobError'
import { getIsNoPlanField } from './getIsNoPlanField'
import { getJustifyField } from './getJustifyField'
import { ICheckFinSobValidationGroup, ICheckFinSobValues } from './models'

interface IProcessFinSobValueValidationParams {
  currentValue: number
  currentJustificationMessage: any
  validationItems: IFinSobCheckItem
  compareToValueProps: IGetFinSobValueProps
  sType: TSType
  isSoB: boolean
  currentValProps: IGetFinSobValueProps
  isCompareToItself: boolean
  props: ICheckFinSobValidationGroup
  category: string
  categoryKey: string
  periodFy: string
  periodFq: string
  fixedFq: number
  validationErrors: IFinSobValidationError[]
}

const getSplitFinSobValue = (valProps: IGetFinSobValueProps, sType: TSType): number => {
  let value = getFinSobValue({ ...valProps })
  if (sType === 't') {
    const totalValue = getFinSobValue({ ...valProps, sType: '' })
    const contractualValue = getFinSobValue({ ...valProps, sType: 'c' })
    value = (totalValue ?? 0) - (contractualValue ?? 0)
  }

  return value
}

/**
 * Check Fin SOB Input (changed values only)
 */
export const checkFinSobValues = (props: ICheckFinSobValues): IFinSobValidationError[] => {
  const validationErrors: IFinSobValidationError[] = []

  try {
    if (!finSobValidationGroups?.length) {
      Logger.log('Fin Sob Validation config is empty.')

      return validationErrors
    }

    const inputKeys: string[] = []
    finSobValidationGroups.forEach(
      (finSobValidationGroup: IFinSobValidationGroup, groupIndex: number) => {
        const groupErrors = checkFinSobValidationGroup({
          ...props,
          finSobValidationGroup,
          groupIndex,
          inputKeys,
          isUseContractuals: props.isUseContractuals,
          isDistributorPlanner: props.isDistributorPlanner,
        })
        if (groupErrors.length) {
          validationErrors.push(...groupErrors)
        }
      }
    )
  } catch (error) {
    Logger.error(`Error check Fin Sob values: ${error}`)
  }

  // Sort InputIndex: asc, Order: desc
  return validationErrors.sort((a, b) => a.inputIndex - b.inputIndex || b.order - a.order)
}

const checkFinSobValidationGroup = (
  props: ICheckFinSobValidationGroup
): IFinSobValidationError[] => {
  const validationErrors: IFinSobValidationError[] = []

  try {
    if (!props.finSobValidationGroup.rules?.length) {
      Logger.log(
        `Fin Sob Validation config for '${props.finSobValidationGroup.key}' group is empty.`
      )

      return validationErrors
    }

    const finSobValidationRules = props.finSobValidationGroup.rules
    finSobValidationRules
      .filter((el) => el.isEnabled)
      .forEach((validationItems: IFinSobCheckItem) => {
        // 1. Check if validation item is enabled
        if (!validationItems.isEnabled) {
          return
        }

        // Default properties for CheckFinSobValues
        const valueType = validationItems.current.type ?? 'targets'
        const currentFy = props.fy
        const isUnits = validationItems.current.value === 'units'
        const isSoB = validationItems.current.value === 'sob'

        // Power splitType is suitable for DistributorPlanner only
        if (
          (!isSoB &&
            props.isUseContractuals &&
            props.isDistributorPlanner &&
            validationItems.current.splitType !== 'Power') ||
          (!props.isDistributorPlanner && validationItems.current.splitType === 'Power')
        ) {
          return
        }

        let isFlagInclude = true
        let isFlagExclude = false
        if (validationItems.partnerFlags?.include?.length) {
          isFlagInclude = validationItems.partnerFlags.include.some((el) =>
            props.partnerFlags.some((pf) => pf === el)
          )
        }
        if (validationItems.partnerFlags?.exclude?.length) {
          isFlagExclude = validationItems.partnerFlags.exclude.some((el) =>
            props.partnerFlags.some((pf) => pf === el)
          )
        }
        if (!isFlagInclude || isFlagExclude) {
          return
        }

        // 2. Get settings
        let settings = props.formats
        if (validationItems.category) {
          if (validationItems.category === OVERALL_CATEGORY) {
            settings = props.formats.filter(
              (el) =>
                el.ProgramType === props.programType &&
                el.Category === props.category &&
                (!el.SubCategory || el.SubCategory === OVERALL_CATEGORY)
            )
          } else {
            settings = props.formats.filter((el) => el.Category === validationItems.category)
          }
        }

        // Mandatory: Override: settingsIndex, fy, fq, sType
        // Optional: Override type
        const commonValueProps: IGetFinSobValueProps = {
          ...props,
          type: valueType,
          sType: '',
          isSoB,
          isUnits,
        }

        settings.forEach((setting) => {
          if ((isSoB && !setting.IsSoBAvailable) || (isUnits && !setting.IsUnitsAvailable)) {
            return
          }

          const settingsIndex = props.formats.indexOf(setting)
          const category =
            !setting.SubCategory || setting.SubCategory === OVERALL_CATEGORY
              ? setting.Category
              : setting.SubCategory
          const categoryKey = `${setting.Category}-${setting.SubCategory}`

          const sTypes: TSType[] = props.isUseContractuals ? ['', 'c', 't'] : ['']
          sTypes.forEach((sType) => {
            // - SoB do not have sType
            // - Contractual is editable for 'c' | 't' cTypes (Contractual and Transactional)
            // - Contractual is editable for '' | 'c' cTypes (Total and Non-Power) (but 't' sType is also validated)
            if (
              (isSoB && sType) ||
              (!isSoB && props.isUseContractuals && !props.isDistributorPlanner && !sType)
              // || (!isSoB && props.isUseContractuals && props.isDistributorPlanner && sType === 't')
            ) {
              return
            }

            const isCompareToItself = validationItems.compareTo.value === 'self'

            F_QUARTERS_FULL.forEach((fq) => {
              FYEAR_OFFSETS.forEach((fYearOffset) => {
                const fixedFq = fq
                // Validation logic for the next 2 years plans
                // 1. Do not check Quarters for fYearOffset > 0 (check FY only)
                if (fYearOffset > 0 && fq !== PLANNING_BY_FY_FQ) {
                  return
                }
                // 2. Do not check FY values for CompareBy.Type = 'actuals'
                if (fYearOffset > 0 && validationItems.compareTo.type === 'actuals') {
                  return
                }
                // 3. Do not check FY values for CompareBy FQ = 'prev'
                if (fq === PLANNING_BY_FY_FQ && validationItems.compareTo.fq === 'prev') {
                  return
                }
                // 4. Do not check FY values for CompareBy FQ = 'average'
                if (fq === PLANNING_BY_FY_FQ && validationItems.compareTo.fq === 'average') {
                  return
                }
                let currentFyOffset = 0
                if (validationItems.current.fy === 'prev') {
                  currentFyOffset = -1
                }
                // 5. Use current.fy === 'prev' rule only for Current FY
                if (currentFyOffset === -1 && fYearOffset > 0) {
                  return
                }
                // 6. Some Rules is only for Future fyears
                if (fYearOffset === 0 && validationItems.options?.isFutureOnly) {
                  return
                }

                // 3. Get values

                const fixedCurrentFy = currentFy + fYearOffset + currentFyOffset

                let compareToType = commonValueProps.type
                let compareToFy = fixedCurrentFy
                let compareToFq = fixedFq
                const compareToIsSoB = validationItems.compareTo.value === 'fin' ? false : isSoB

                if (validationItems.compareTo.type === 'actuals') {
                  compareToType = 'actuals'
                }
                const periodFy = validationItems.compareTo.fy ?? ''
                if (validationItems.compareTo.fy === 'prev') {
                  compareToFy = fixedCurrentFy - 1
                }
                const periodFq = validationItems.compareTo.fq ?? ''
                if (validationItems.compareTo.fq === 'prev') {
                  compareToFq = fixedFq - 1
                  if (compareToFq === 0) {
                    compareToFq = 4
                    compareToFy = fixedCurrentFy - 1
                  }
                }

                // Prepare props for get Values

                const currentValProps: IGetFinSobValueProps = {
                  ...commonValueProps,
                  settingsIndex,
                  fy: fixedCurrentFy,
                  fq: fixedFq,
                  sType,
                }

                let sTypeField = isUnits
                  ? CustomFields.IsChangedUnitsManual
                  : CustomFields.IsChangedManual
                if (currentValProps.sType) {
                  if (isUnits) {
                    sTypeField =
                      currentValProps.sType === 't'
                        ? CustomFields.IsChangedUnitsTManual
                        : CustomFields.IsChangedUnitsCManual
                  } else {
                    sTypeField =
                      currentValProps.sType === 't'
                        ? CustomFields.IsChangedTManual
                        : CustomFields.IsChangedCManual
                  }
                }
                const isChangedCurrValProps: IGetFinSobValueProps = {
                  ...currentValProps,
                  fieldName: isSoB ? CustomFields.IsChangedSoBManual : sTypeField,
                }

                const justifyField = getJustifyField({
                  isSoB,
                  isUnits,
                  sType: currentValProps.sType,
                })
                const justificationMessageProps: IGetFinSobValueProps = {
                  ...currentValProps,
                  fieldName: justifyField,
                }

                const isContr = props.isUseContractuals && currentValProps.sType === 'c'
                const isNoPlanField = getIsNoPlanField({ isContr })
                const isNoPlanProps: IGetFinSobValueProps = {
                  ...currentValProps,
                  type: 'targets',
                  fieldName: isNoPlanField,
                }

                const compareToValueProps: IGetFinSobValueProps = {
                  ...commonValueProps,
                  settingsIndex,
                  fy: compareToFy,
                  fq: compareToFq,
                  sType,
                  type: compareToType,
                  isSoB: compareToIsSoB,
                }

                // Get Current Values

                const currentValue = getSplitFinSobValue({ ...currentValProps }, sType)
                const currentValueIsChanged = !!getFinSobValue({ ...isChangedCurrValProps })
                const isNoPlan: any = getFinSobValue({ ...isNoPlanProps })
                const currentJustificationMessage: any = getFinSobValue({
                  ...justificationMessageProps,
                })

                const processFinSobValueValidationParams = {
                  currentValue,
                  currentJustificationMessage,
                  validationItems,
                  compareToValueProps,
                  sType,
                  isSoB,
                  currentValProps,
                  isCompareToItself,
                  props,
                  category,
                  categoryKey,
                  periodFy,
                  periodFq,
                  fixedFq,
                  validationErrors,
                }

                if (currentValueIsChanged && !isNoPlan) {
                  processFinSobValueValidation(processFinSobValueValidationParams)

                  // Use lastPrevVsFuture rule only for Future FY (disabled by IS_CHECK_LAST_PREV_VS_FUTURE flag)
                  if (
                    validationItems.options?.lastPrevVsFuture &&
                    fYearOffset > 0 &&
                    IS_CHECK_LAST_PREV_VS_FUTURE
                  ) {
                    // Espicially for lastPrevVsFuture rule: compare Future FY vs Last Prev FY
                    processFinSobValueValidation({
                      ...processFinSobValueValidationParams,
                      compareToValueProps: {
                        ...compareToValueProps,
                        fy: currentFy - 1,
                      },
                    })
                  }
                } // Is Value changed
              }) // FYears
            }) // F_QUARTERS

            // 5. Additional check: PrevFY Fin Actuals > $1M && SOB < 10%
            /*
              // Temporally commented before debug Contractual/Transactional mode
              if (!itemIdx && currentValueIsChanged) {
                // Default properties for PrevFY USD Actuals
                const finActualsProps: IGetFinSobValueProps = {
                  ...props,
                  type: 'actuals',
                  fy: fixedCurrentFy - 1,
                  fq: PLANNING_BY_FY_FQ,
                  isSoB: false,
                  currency: 'USD',
                }

                const finActualsValue = getFixedFinSobValue({ ...finActualsProps }, sType)
                const finActualsValueName = `Total ${compareToValueShortType} FY${finActualsProps.fy % 100}`

                const validationError = checkSellThruFinSobError({
                  category,
                  categoryKey,
                  finSob: currentValue,
                  finSellThru: finActualsValue,
                  settingIndex: currentValProps.settingsIndex,
                  valueType: currentValProps.type,
                  year: currentValProps.fy,
                  quarter: currentValProps.fq,
                  currentValueName,
                  comparisonValueName: finActualsValueName,
                  justificationMessage: currentJustificationMessage,
                })
                if (validationError) {
                  const inputKey = getInputKey({
                    ...props,
                    settingsIndex: currentValProps.settingsIndex,
                    fy: currentValProps.fy,
                    fq: currentValProps.fq,
                    type: currentValProps.type,
                    isSoB: currentValProps.isSoB,
                  })

                  const inputIndex = getInputIndex(inputKey, props.inputKeys)
                  validationErrors.push({
                    ...validationError,
                    inputKey,
                    inputIndex,
                    compareToKey: getCompareToKey(periodFy, periodFq),
                    groupIndex: props.groupIndex,
                    isSob: currentValProps.isSoB,
                    isChecked: false,

                    sType: currentValProps.sType,

                    currentValueType,
                    currentValuePeriod,

                    comparisonValueType: compareToValueType,
                    comparisonValuePeriod: compareToValuePeriod,
                    compareToValueType: compareToValueProps.type,
                    compareToYear: compareToValueProps.fy,
                    compareToQ: compareToValueProps.fq,
                  })
                }
              }
              */
          }) // sType
        }) // setting
      }) // finSobValidationRules
  } catch (error) {
    Logger.error(`Error check Fin Sob values: ${error}`)
  }

  return validationErrors.sort((a, b) => b.order - a.order)
}

/**
 * Process Fin SOB value validation
 */
const processFinSobValueValidation = (params: IProcessFinSobValueValidationParams): void => {
  const {
    currentValue,
    currentJustificationMessage,
    validationItems,
    compareToValueProps,
    sType,
    isSoB,
    currentValProps,
    isCompareToItself,
    props,
    category,
    categoryKey,
    periodFy,
    periodFq,
    fixedFq,
    validationErrors,
  } = params

  let compareToValue = 0
  const isCompareToFqAverage = validationItems.compareTo.fq === 'average'
  if (validationItems.compareTo.value !== 'self') {
    if (!isCompareToFqAverage) {
      compareToValue = getSplitFinSobValue({ ...compareToValueProps }, sType)
    } else {
      let sum = 0
      F_QUARTERS_ONLY.forEach((toValueFq) => {
        sum += getSplitFinSobValue({ ...compareToValueProps, fq: toValueFq }, sType)
      })
      compareToValue = sum / 4
    }
  }

  const valNamePrefix = isSoB ? 'SOB ' : ''
  const currentValueShortType = currentValProps.type === 'actuals' ? 'Actuals' : 'Plan'
  const currentValueType = `${valNamePrefix}${currentValueShortType}`

  const currentValuePeriodFq =
    currentValProps.fq === PLANNING_BY_FY_FQ ? '' : ` Q${currentValProps.fq}`
  const currentValuePeriod = `FY${currentValProps.fy % 100}${currentValuePeriodFq}`
  const currentValueName = `${currentValueType} ${currentValuePeriod}`

  const compareToValueShortType = compareToValueProps.type === 'actuals' ? 'Actuals' : 'Plan'
  const compareToValueType = `${valNamePrefix}${compareToValueShortType}`

  const compareToValuePeriodFqPart =
    compareToValueProps.fq === PLANNING_BY_FY_FQ ? '' : ` Q${compareToValueProps.fq}`
  const compareToValuePeriodFq = isCompareToFqAverage ? ' Average' : compareToValuePeriodFqPart
  const compareToValuePeriod = `FY${compareToValueProps.fy % 100}${compareToValuePeriodFq}`

  // 4. Compare values

  // Debug
  // Logger.debug(
  //   `${currentValProps.isSoB ? 'SOB' : 'Fin'} ${currentValProps.type} ${currentValProps.sType} ${currentValProps.fy - 2000}.${currentValProps.fq} (${currentValue}) / ` +
  //     `${compareToValueProps.isSoB ? 'SOB' : 'Fin'} ${compareToValueProps.type} ${compareToValueProps.sType} ${compareToValueProps.fy - 2000}.${compareToValueProps.fq} (${compareToValue})` +
  //     ` / ${validationItems.key}`
  // )
  // if (validationItems.key === 'jbp-planning-check-sob-plan-prev-fy') {
  //   Logger.debug(`${validationItems.key}`)
  // }

  // FY25: check condition:
  // - Do not compare to 0 value or if current Actuals value = 0
  //   (compareToValue !== 0 && (currentValProps.type !== 'actuals' || currentValue !== 0)) ||
  //
  // FY26: check condition:
  // - Do not compare null/0 value with any other and do not compare to null/0 value
  //
  if (
    (!!compareToValue &&
      (!!currentValue || (!!validationItems.current.isZeroInput && currentValue === 0))) ||
    isCompareToItself
  ) {
    let isError = false
    let isWarning = false
    let compareToKey = ''

    let pointDiff = Math.abs(currentValue - compareToValue) // Calc difference for SoB values
    if (!isSoB) {
      // Calc difference for Fin values
      pointDiff = Math.abs(calcPercentage(compareToValue, currentValue) ?? 0)
    }

    if (isCompareToItself) {
      const isValueError =
        (validationItems.thresholds.error === 0 && currentValue === 0) ||
        (validationItems.thresholds.error === -1 && currentValue < 0)

      const isCheckSelfInFutureFQ = validationItems.current.isFutureFq && IS_CHECK_SELF_IN_FUTURE_FQ
      if (isCheckSelfInFutureFQ) {
        if (isValueError && fixedFq >= props.currentFQuarter) {
          // Check Plans 0 for future FQ
          isError = true
        }
      } else if (!validationItems.current.isFutureFq && isValueError) {
        // Regular Partner and Distributior Planner
        // checks future FQ w/o isFutureFq flag, instead using separated isFutureFq logic
        isError = true
      }
    } else if (validationItems.current.isZeroInput) {
      if (currentValue === 0 && compareToValue > 0) {
        isError = true
        compareToKey = validationItems.key
      }
    } else {
      isError = pointDiff >= validationItems.thresholds.error
      isWarning = pointDiff >= validationItems.thresholds.warning
    }

    if (isError || isWarning) {
      addFinSobError({
        isError,
        isWarning,
        isSoB,
        props,
        validationItems,
        category,
        categoryKey,
        compareToKey,
        periodFy,
        periodFq,
        pointDiff,
        currentValue,
        currentValueName,
        currentValueType,
        currentValuePeriod,
        compareToValue,
        compareToValueType,
        compareToValuePeriod,
        currentJustificationMessage,
        currentValProps,
        sType,
        compareToValueProps,
        isCompareToItself,
        validationErrors,
      })
    }
  }
}
